import React, { useState, useEffect } from 'react';
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Header            from './components/Header';
import BottomNav from './components/BottomNav';
import Footer            from './components/Footer';
import PromoBanner       from './components/PromoBanner';
import ErrorBoundary     from './components/ErrorBoundary';
import ProtectedRoute    from './components/ProtectedRoute';
import Occasion from './pages/Occasion';
import OccasionDetail from './pages/OccasionDetail';
import Reparateurs from './pages/Reparateurs';
import NouvelAppelOffre from './pages/NouvelAppelOffre';
import MesAnnonces from './pages/MesAnnonces';
import CompareBar        from './components/CompareBar';
import CliniqAgent       from './components/CliniqAgent';

import Home              from './pages/Home';
import CategoryPage      from './pages/CategoryPage';
import ProductDetail     from './pages/ProductDetail';
import StorePage         from './pages/StorePage';
import Cart              from './pages/Cart';
import Checkout          from './pages/Checkout';
import SupplierLanding   from './pages/SupplierLanding';
import CategoriesAll     from './pages/CategoriesAll';
import Login             from './pages/Login';
import Bootstrap         from './pages/Bootstrap';
import Register          from './pages/Register';
import AuthCallback      from './pages/AuthCallback';
import MyOrders          from './pages/MyOrders';
import Messages          from './pages/Messages';
import Favorites         from './pages/Favorites';
import SupplierDashboard from './pages/SupplierDashboard';
import SupplierBuyers    from './pages/SupplierBuyers';
import AdminDashboard    from './pages/AdminDashboard';
import Compare           from './pages/Compare';
import NotFound          from './pages/NotFound';
import ResetPassword     from './pages/ResetPassword';
import { About, HowItWorksPage, Pricing, Contact, FAQ, Shipping, Terms, Legal, Privacy, Cookies } from './pages/StaticPages';
import { BlogList, BlogPost } from './pages/Blog';

import { Toaster }         from './components/ui/toaster';
import { useToast }        from './hooks/use-toast';
import { AuthProvider }    from './context/AuthContext';
import { CompareProvider } from './context/CompareContext';
import { SiteConfigProvider } from './context/SiteConfigContext';
import { AdminEditProvider } from './context/AdminEditContext';
import AdminEditToolbar from './components/AdminEditToolbar';

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return <AppContent />;
}

function AppContent() {
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dz_cart') || '[]'); } catch { return []; }
  });
  const { toast } = useToast();

  useEffect(() => {
    localStorage.setItem('dz_cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cart-changed'));
  }, [cart]);

  const onAddToCart = (product, variant, qty = 1) => {
    setCart(prev => {
      const idx = prev.findIndex(it => it.product.id === product.id && it.variant.label === variant.label);
      if (idx >= 0) { const next = [...prev]; next[idx] = { ...next[idx], qty: next[idx].qty + qty }; return next; }
      return [...prev, { product, variant, qty }];
    });
    toast({ title: 'Ajouté au panier', description: `${product.name} — ${variant.label}` });
  };

  const cartCount = cart.reduce((s, it) => s + it.qty, 0);

  return (
    <div className="App">
      <PromoBanner />
      <Header cartCount={cartCount} />
      <main className="pb-20">
        <ErrorBoundary>
          <Routes>
            {/* Publiques */}
            <Route path="/"                    element={<Home onAddToCart={onAddToCart} />} />
            <Route path="/categories"          element={<CategoriesAll />} />
            <Route path="/categorie/:id"       element={<CategoryPage onAddToCart={onAddToCart} />} />
            <Route path="/produit/:id"         element={<ProductDetail onAddToCart={onAddToCart} />} />
            <Route path="/store/:slug"         element={<StorePage onAddToCart={onAddToCart} />} />
            <Route path="/panier"              element={<Cart cart={cart} setCart={setCart} />} />
            <Route path="/devenir-fournisseur" element={<Navigate to="/inscription?role=supplier" replace />} />
            <Route path="/connexion"           element={<Login />} />
            <Route path="/bootstrap-admin"     element={<Bootstrap />} />
            <Route path="/inscription"         element={<Register />} />
            <Route path="/recherche"           element={<CategoryPage onAddToCart={onAddToCart} />} />
            <Route path="/occasion" element={<Occasion />} />
            <Route path="/occasion/:id" element={<OccasionDetail />} />
            <Route path="/reparateurs" element={<Reparateurs />} />
            <Route path="/appels-offres/nouveau" element={<NouvelAppelOffre />} />
            <Route path="/mes-annonces" element={<MesAnnonces />} />
            <Route path="/comparer"            element={<Compare onAddToCart={onAddToCart} />} />
            <Route path="/reset-password"      element={<ResetPassword />} />
            {/* Pages statiques */}
            <Route path="/a-propos"          element={<About />} />
            <Route path="/comment-ca-marche" element={<HowItWorksPage />} />
            <Route path="/tarifs"            element={<Pricing />} />
            <Route path="/contact"           element={<Contact />} />
            <Route path="/faq"               element={<FAQ />} />
            <Route path="/livraison"         element={<Shipping />} />
            <Route path="/cgv"               element={<Terms />} />
            <Route path="/mentions-legales"  element={<Legal />} />
            <Route path="/confidentialite"   element={<Privacy />} />
            <Route path="/cookies"           element={<Cookies />} />
            <Route path="/blog"              element={<BlogList />} />
            <Route path="/blog/:slug"        element={<BlogPost />} />
            {/* Connecté */}
            <Route path="/checkout"      element={<ProtectedRoute><Checkout cart={cart} setCart={setCart} /></ProtectedRoute>} />
            <Route path="/mes-commandes" element={<ProtectedRoute><MyOrders /></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
            <Route path="/favoris"       element={<ProtectedRoute><Favorites onAddToCart={onAddToCart} /></ProtectedRoute>} />
            {/* Fournisseur */}
            <Route path="/fournisseur"           element={<ProtectedRoute role="supplier"><SupplierDashboard /></ProtectedRoute>} />
            <Route path="/fournisseur/dentistes" element={<ProtectedRoute role="supplier"><SupplierBuyers /></ProtectedRoute>} />
            {/* Admin */}
            <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </ErrorBoundary>
      </main>
      <BottomNav cartCount={cartCount} />
      <CliniqAgent />
      <CompareBar />
      <Footer />
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <SiteConfigProvider>
        <AuthProvider>
          <AdminEditProvider>
            <CompareProvider>
              <AppRouter />
              <AdminEditToolbar />
            </CompareProvider>
          </AdminEditProvider>
        </AuthProvider>
      </SiteConfigProvider>
    </BrowserRouter>
  );
}

export default App;
